import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";
import {
  admin_internship_applications_detail,
  admin_internship_application_verify,
  superadmin_internship_applications_detail
} from "../admin.api";
import "./style.css";

const getStatusInfo = (status) => {
  const statusMap = {
    1: {
      label: "Approved",
      class: "successs",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      )
    },
    2: {
      label: "Reverted",
      class: "danger",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M12 8v4m0 4h.01"></path>
        </svg>
      )
    },
    3: {
      label: "Rejected",
      class: "danger",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      )
    },
  };
  const defaultIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
  );
  return statusMap[status] || {
    label: "Pending",
    class: "pending",
    icon: defaultIcon
  };
};

export default function InternshipApplicationDetail(props) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchDetail();
    }
  }, [id]);

  const fetchDetail = () => {
    setIsLoading(true);
    const adminType = Number(localStorage.getItem("adminType"));
    const apiCall =
      adminType === 2
        ? superadmin_internship_applications_detail(id)
        : admin_internship_applications_detail(id)

    apiCall
      .then((res) => {
        if (res?.success && res?.data) {
          setData(res.data);
        } else {
          setSnackMessage("Failed to load application details");
          setIsSnackError(true);
          setSnackOpen(true);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching detail:", error);
        setSnackMessage("Error loading application details");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsLoading(false);
      });
  };

  const handleVerify = (status) => {
    let title = "";
    let confirmButtonText = "";
    let confirmButtonColor = "";

    if (status === 1) {
      title = "Approve Application?";
      confirmButtonText = "Yes, Approve";
      confirmButtonColor = "#28a745";
    } else if (status === 2) {
      title = "Revert Application?";
      confirmButtonText = "Yes, Revert";
      confirmButtonColor = "#ffc107";
    } else {
      title = "Reject Application?";
      confirmButtonText = "Yes, Reject";
      confirmButtonColor = "#dc3545";
    }

    Swal.fire({
      title: title,
      text: status === 1 ? "The student will be eligible for rankings." : "Please provide a reason for the student.",
      input: status !== 1 ? "textarea" : null,
      inputPlaceholder: "Type your message here...",
      showCancelButton: true,
      confirmButtonText: confirmButtonText,
      confirmButtonColor: confirmButtonColor,
      inputValidator: (value) => {
        if (status !== 1 && !value) {
          return "You need to write something!";
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setIsUpdating(true);
        const payload = {
          status: status,
          admin_message: typeof result.value === "string" ? result.value : "",
        };

        admin_internship_application_verify(id, payload)
          .then((res) => {
            if (res?.success) {
              setSnackMessage(res.message || "Status updated successfully");
              setIsSnackError(false);
              setSnackOpen(true);
              props.history.push("/admin-internship-applications");
            } else {
              setSnackMessage(res.message || "Failed to update status");
              setIsSnackError(true);
              setSnackOpen(true);
            }
            setIsUpdating(false);
          })
          .catch((error) => {
            setSnackMessage("Error updating status");
            setIsSnackError(true);
            setSnackOpen(true);
            setIsUpdating(false);
          });
      }
    });
  };

  const snackbarClose = () => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
  };

  const getBaseUrl = () => {
    return process?.env?.REACT_APP_BASE_URL;
  };

  if (isLoading) return <Loader open={true} />;

  if (!data) return (
    <div className="container mt-5">
      <div className="alert alert-warning">
        <p>Failed to load application details.</p>
        <button className="btn btn-primary btn-sm" onClick={() => props.history.goBack()}>Go Back</button>
      </div>
    </div>
  );

  const statusInfo = getStatusInfo(data.status);

  return (
    <div className="registered-internships-page">
      <Loader open={isUpdating} />
      <Snackbar message={snackMessage} error={isSnackError} open={snackOpen} close={snackbarClose} />

      <div className="container-fluid pt-4 mb-5 px-md-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-minimal-outline btn-sm"
              onClick={() => props.history.push("/admin-internship-applications")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Back to List
            </button>
            {/* <h4 className="fw-bold text-dark mb-0">Application Entry #{data.id}</h4> */}
          </div>

          {(data.status === 0 || data.status === 2) && (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-success px-4"
                onClick={() => handleVerify(1)}
              >
                Approve
              </button>
              {/* {data.status === 0 && (
                <button
                  className="btn btn-sm btn-warning px-4"
                  onClick={() => handleVerify(2)}
                >
                  Revert
                </button>
              )} */}
              <button
                className="btn btn-sm btn-danger px-4"
                onClick={() => handleVerify(3)}
              >
                Reject
              </button>
            </div>
          )}
        </div>

        <div className="row g-4">
          {/* Left Column: Summary Sidebar */}
          <div className="col-xl-3 col-lg-4 mb-4">
            <div className="section-card-minimal">
              <div className="section-header">Applicant Overview</div>
              <div className="card-body p-4 text-center">
                <div className="mb-4">
                  {data.file_upload_photo ? (
                    <img
                      src={data.file_upload_photo.startsWith('http') ? data.file_upload_photo : `${getBaseUrl()}${data.file_upload_photo}`}
                      alt="Profile"
                      className="profile-img-minimal"
                    />
                  ) : (
                    <div className="profile-img-minimal bg-light d-inline-flex align-items-center justify-content-center mx-auto">
                      <span className="h1 text-muted mb-0">{data.full_name?.charAt(0)}</span>
                    </div>
                  )}
                </div>
                <h5 className="fw-bold mb-1">{data.full_name}</h5>
                <p className="small text-muted mb-4 opacity-75">Ref ID: {data.id}</p>

                <div className="text-start">
                  <div className="info-item-minimal mb-3">
                    <label>Verfication Status</label>
                    <span className={`status-badge ${statusInfo.class} mt-1`}>
                      <span className="status-icon">{statusInfo.icon}</span>
                      <span className="status-label">{statusInfo.label}</span>
                    </span>
                  </div>
                  <div className="info-item-minimal mb-3">
                    <label>Email Address</label>
                    <span className="text-break">{data.email}</span>
                  </div>
                  <div className="info-item-minimal mb-3">
                    <label>Contact Number</label>
                    <span>{data.phone_no}</span>
                  </div>
                  <div className="info-item-minimal mb-3">
                    <label>Application Date</label>
                    <span>{moment(data.created_at).format("DD MMM YYYY, hh:mm A")}</span>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-top">
                  {data.resume && (
                    <a href={data.resume.startsWith('http') ? data.resume : `${getBaseUrl()}${data.resume}`} target="_blank" rel="noreferrer" className="btn btn-minimal-dark w-100 mb-2">
                      View CV / Resume
                    </a>
                  )}
                  {data.rec_letter_name && (
                    <a href={data.rec_letter_name.startsWith('http') ? data.rec_letter_name : `${getBaseUrl()}${data.rec_letter_name}`} target="_blank" rel="noreferrer" className="btn btn-minimal-dark w-100">
                      View Recommendation Letter
                    </a>
                  )}
                </div>
              </div>
            </div>
            {/* 
            {data.user && (
              <div className="section-card-minimal mt-4 shadow-sm border-0">
                <div className="section-header bg-light">User Account</div>
                <div className="card-body p-3">
                  <div className="info-item-minimal mb-2">
                    <label>Account Name</label>
                    <span>{data.user.name}</span>
                  </div>
                  <div className="info-item-minimal">
                    <label>Linked Mobile</label>
                    <span>{data.user.mobile_number}</span>
                  </div>
                </div>
              </div>
            )} */}

            {data.admin_remarks && (
              <div className="section-card-minimal mt-4 border-warning shadow-sm">
                <div className="section-header bg-warning text-dark border-warning">Internal Feedback</div>
                <div className="card-body p-3">
                  <p className="small mb-0 text-dark">"{data.admin_remarks}"</p>
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Detailed Info */}
          <div className="col-xl-9 col-lg-8">


            <div className="section-card-minimal">

              <div className="section-header">Candidate Profile</div>
              <div className="info-grid-minimal">
                <div className="info-item-minimal">
                  <label>Gender</label>
                  <span>{data.gender || "Not specified"}</span>
                </div>
                <div className="info-item-minimal">
                  <label>Date of Birth</label>
                  <span>{moment(data.date_of_birth).format("DD MMM YYYY")}</span>
                </div>
                <div className="info-item-minimal">
                  <label>Native of Kerala</label>
                  <span>{data.is_kerala_native ? "Yes" : "No"}</span>
                </div>
                <div className="info-item-minimal">
                  <label>Course Track</label>
                  <span className="text-capitalize">{data.course_completion_status}</span>
                </div>
                <div className="info-item-minimal col-span-2">
                  <label>Residential Address</label>
                  <span>{data.address}</span>
                </div>
              </div>
            </div>

            {/* Academic Section with Full Semester/Year Details */}
            <div className="section-card-minimal mt-4">
              <div className="section-header">Evaluation & Academic History</div>
              <div className="card-body p-0">
                {data.intern_qualification?.map((q, idx) => (
                  <div key={idx} className={idx > 0 ? "border-top p-0" : "p-0"}>
                    <div className="bg-light p-3 border-bottom d-flex justify-content-between align-items-center">
                      <div>
                        <span className="badge bg-white text-dark border me-2">{q.qualification_type?.name}</span>
                        <strong className="text-uppercase small">{q.qualification?.name}</strong>
                      </div>
                      {q.certificate_photo && (
                        <a href={q.certificate_photo.startsWith('http') ? q.certificate_photo : `${getBaseUrl()}${q.certificate_photo}`} target="_blank" rel="noreferrer" className="btn btn-minimal-dark btn-sm py-1 font-8">
                          View Certificate
                        </a>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="row mb-4">
                        <div className="col-md-4 mb-3">
                          <label className="text-muted x-small text-uppercase fw-bold mb-1 d-block">Stream / Discipline</label>
                          <span className="fw-bold">{q.stream?.name || "--"}</span>
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="text-muted x-small text-uppercase fw-bold mb-1 d-block">Board / University</label>
                          <span>{q.board}</span>
                        </div>
                        <div className="col-md-2 mb-3">
                          <label className="text-muted x-small text-uppercase fw-bold mb-1 d-block">Year Passed</label>
                          <span>{q.year_passed}</span>
                        </div>
                        <div className="col-md-2 mb-3">
                          <label className="text-muted x-small text-uppercase fw-bold mb-1 d-block">Scoring Type</label>
                          <span className="text-capitalize">{q.mark_system?.replace('_', ' ')}</span>
                        </div>
                      </div>

                      {q.marks && q.marks.length > 0 && (
                        <div className="table-responsive rounded-3 overflow-hidden shadow-sm mt-3" style={{ border: '1px solid #333' }}>
                          <table className="table table-sm table-hover mb-0">
                            <thead className="bg-light">
                              <tr style={{ borderBottom: '1px solid #333' }}>
                                <th className="px-3 py-2 border-0 x-small text-muted font-weight-bold">Academic Period</th>
                                <th className="py-2 border-0 x-small text-muted font-weight-bold">Result Detail</th>
                                <th className="py-2 border-0 x-small text-muted font-weight-bold text-center">Score / Performance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {q.marks.map((m, mIdx) => (
                                <tr key={mIdx} className="align-middle">
                                  <td className="px-3 py-2 fw-bold text-secondary small" style={{ borderBottom: mIdx === q.marks.length - 1 ? 'none' : '1px solid #eee' }}>{m.period_label}</td>
                                  <td className="py-2 small text-muted font-weight-bold" style={{ borderBottom: mIdx === q.marks.length - 1 ? 'none' : '1px solid #eee' }}>
                                    {q.mark_system === 'semester_gpa' || q.mark_system === 'cgpa' ? "Semester Point Average" : "Cumulative Marks Distribution"}
                                  </td>
                                  <td className="py-2 text-center" style={{ borderBottom: mIdx === q.marks.length - 1 ? 'none' : '1px solid #eee' }}>
                                    {q.mark_system === 'semester_gpa' || q.mark_system === 'cgpa' ? (
                                      <span className="badge bg-dark-soft text-dark px-3 py-1 font-8 fw-bold">GPA: {m.cgpa || '--'}</span>
                                    ) : (
                                      <div className="d-flex flex-column align-items-center">
                                        <div className="fw-bold small">{m.marks_obtained} / {m.total_marks}</div>
                                        <div className="x-small text-muted font-weight-normal">{m.total_marks ? ((m.marks_obtained / m.total_marks) * 100).toFixed(1) + '%' : ''}</div>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internship Proposals Expansion */}
            <div className="section-card-minimal">
              <div className="section-header">Candidate Preferences & Proposals</div>
              <div className="card-body p-0">
                {data.proposals && [...data.proposals].sort((a, b) => a.preference_order - b.preference_order).map((p, idx) => (
                  <div key={idx} className={`p-4 ${idx > 0 ? 'border-top' : ''}`}>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <span className="badge rounded-circle bg-dark text-white d-inline-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px', fontSize: '10px' }}>0{p.preference_order}</span>
                          <h6 className="fw-bold mb-0 text-primary">{p.internship_request?.internship_discipline}</h6>
                        </div>
                        <p className="text-secondary small mb-2"><i className="bi bi-building me-1"></i> {p.internship_request?.company?.company_name}</p>
                      </div>
                      <div className="text-end">
                        <span className={`badge ${p.status === 'pending' ? 'bg-warning-soft text-warning' : 'bg-success-soft text-success'} py-2 px-3 mb-1`} style={{ borderRadius: '8px' }}>
                          {p.status?.toUpperCase()}
                        </span>
                        <div className="text-dark-soft x-small fw-bold">Internal Global Rank: {p.admin_rank || "--"}</div>
                      </div>
                    </div>

                    <div className="bg-light rounded-3 p-3 mt-3">
                      <div className="row">
                        <div className="col-md-6 border-end">
                          <div className="mb-2">
                            <label className="x-small text-muted text-uppercase fw-bold d-block mb-1">Brief Description</label>
                            <p className="small text-dark opacity-100 mb-0">{p.internship_request?.brief_description || "No description provided."}</p>
                          </div>
                          <div>
                            <label className="x-small text-muted text-uppercase fw-bold d-block mb-1">Grading Criteria</label>
                            <p className="small mb-0 text-dark-soft">{p.internship_request?.grade_criteria || "None"}</p>
                          </div>
                        </div>
                        <div className="col-md-6 ps-md-4">
                          <div className="row g-2">
                            <div className="col-6">
                              <label className="x-small text-muted text-uppercase fw-bold d-block mb-1">Target Category</label>
                              <div className="d-flex flex-wrap gap-2 mt-1">
                                {p.internship_request?.target_category === 'both' ? (
                                  <>
                                    <span className="badge-target-elite">Students Completed Course</span>
                                    <span className="badge-target-elite">Students Ongoing Course</span>
                                  </>
                                ) : (
                                  <span className="badge-target-elite">
                                    {p.internship_request?.target_category?.replace(/_/g, ' ') || "Both"}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="col-6">
                              <label className="x-small text-muted text-uppercase fw-bold d-block mb-1">Students Req.</label>
                              <span className="small fw-bold">{p.internship_request?.number_of_students} Positions</span>
                            </div>
                            <div className="col-12 mt-2 pt-2 border-top">
                              <label className="x-small text-muted text-uppercase fw-bold d-block mb-1">Preferred Duration</label>
                              <span className="small text-muted">
                                {moment(p.internship_request?.preferred_start_date).format("DD MMM YYYY")} - {moment(p.internship_request?.preferred_end_date).format("DD MMM YYYY")}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
